import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Divider
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';

// Helper function to format numbers with decimal and thousands separators
const formatNumber = (value, format) => {
  if (!format || value === null || value === undefined) return value;
  const numValue = Number(value);
  if (isNaN(numValue)) return value;

  let decimals = 2;
  let decimalSeparator = ',';
  let thousandsSeparator = '.';

  // Handle string format like "0.0", "0.00", "0"
  if (typeof format === 'string') {
    const match = format.match(/^0(\.0+)?$/);
    if (match) {
      decimals = match[1] ? match[1].length - 1 : 0;
    }
  } else if (typeof format === 'object') {
    decimals = format.decimals ?? 2;
    decimalSeparator = format.decimalSeparator || ',';
    thousandsSeparator = format.thousandsSeparator || '.';
  }

  const rounded = Number(numValue.toFixed(decimals));
  const [intPart, decPart] = rounded.toFixed(decimals).split('.');
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

  return decPart ? `${formattedInt}${decimalSeparator}${decPart}` : formattedInt;
};

const SetValue = ({
  text = '',
  tagId = null,
  tagValue = null,
  placeholder = 'Değer girin...',
  buttonText = 'Set',
  showButton = false,
  editable = true,
  unit = '',
  numberFormat = null, // { decimals, decimalSeparator, thousandsSeparator }
  textAlign = 'center',
  companyId,
  onValueSet,
  // Style properties
  backgroundColor,
  foreColor,
  borderWidth,
  borderColor,
  borderRadius,
  fontSize,
  fontWeight,
  fontFamily,
  opacity,
  textColor,
  ...props
}) => {
  // Resolve companyId from session/local storage if not provided
  const effectiveCompanyId = companyId || (() => {
    const s = sessionStorage.getItem('TEMPCOM');
    if (s) { try { return parseInt(JSON.parse(s)); } catch { return parseInt(s) || 3; } }
    const l = localStorage.getItem('temp_com_id');
    return l ? parseInt(l) : 3;
  })();

  const [modalOpen, setModalOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [currentTagValue, setCurrentTagValue] = useState(tagValue);

  useEffect(() => {
    setCurrentTagValue(tagValue);
  }, [tagValue]);

  const handleOpenModal = (e) => {
    if (!tagId) {
      toast.warning('Tag atanmamış');
      return;
    }
    e.stopPropagation();
    setInputValue('');
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setInputValue('');
  };

  const handleSetValue = async () => {
    if (!tagId) {
      toast.warning('Tag atanmamış');
      return;
    }

    if (inputValue === '') {
      toast.warning('Lütfen bir değer girin');
      return;
    }

    try {
      // Call API to set tag value - using correct endpoint
      // API expects: TagId (string), TagValue (string), companyId (int)
      console.log('[SetValue] Sending:', { TagId: tagId, TagValue: inputValue, companyId: effectiveCompanyId });
      const response = await fetch('https://app-rmmscmdapi.azurewebsites.net/api/cmd/SetValue', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          TagId: String(tagId),
          TagValue: String(inputValue),
          companyId: effectiveCompanyId
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result === true || result === 'true') {
          toast.success(`Değer başarıyla ayarlandı: ${inputValue}`);
          setCurrentTagValue(inputValue);
          handleCloseModal();

          if (onValueSet) {
            onValueSet(inputValue);
          }
        } else {
          toast.error('Değer ayarlanamadı - API yanıtı: ' + JSON.stringify(result));
        }
      } else {
        const errorText = await response.text();
        toast.error('Değer ayarlanamadı: ' + errorText);
      }
    } catch (error) {
      console.error('Error setting tag value:', error);
      toast.error(`Hata: ${error.message}`);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSetValue();
    }
  };

  // Context menu handler - dispatches global event
  const handleContextMenu = (event) => {
    if (tagId) {
      event.preventDefault();
      event.stopPropagation();

      // Dispatch global event to open Tag Info Modal
      const displayName = (text && text !== 'Label') ? text : `Tag ${tagId}`;
      const tagInfoData = {
        tagId: tagId,
        tagName: displayName,
        tagName2: '',
        currentValue: currentTagValue,
        unit: unit || ''
      };
      window.dispatchEvent(new CustomEvent('openTagInfo', { detail: tagInfoData }));
    }
  };

  const formattedValue = numberFormat ? formatNumber(currentTagValue, numberFormat) : currentTagValue;
  const displayValue = currentTagValue !== null && currentTagValue !== undefined
    ? `${formattedValue}${unit ? ' ' + unit : ''}`
    : placeholder;

  const wrapperStyle = {
    width: '100%',
    height: '100%',
    backgroundColor: backgroundColor || '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: textAlign === 'left' ? 'flex-start' : textAlign === 'right' ? 'flex-end' : 'center',
    padding: '4px',
    border: `${borderWidth || 2}px solid ${borderColor || '#000000'}`,
    borderRadius: `${borderRadius || 8}px`,
    opacity: opacity !== undefined ? opacity : 1,
    cursor: tagId ? 'pointer' : 'default',
    boxSizing: 'border-box',
    '&:hover': {
      backgroundColor: tagId ? '#f0f0f0' : backgroundColor
    }
  };

  const textStyle = {
    fontSize: fontSize || 14,
    fontWeight: fontWeight || 400,
    fontFamily: fontFamily || 'inherit',
    color: textColor || foreColor || '#000000',
    textAlign: textAlign || 'center',
    userSelect: 'none'
  };

  return (
    <>
      <Box
        sx={wrapperStyle}
        onClick={handleOpenModal}
        onContextMenu={handleContextMenu}
      >
        <Typography sx={textStyle}>
          {displayValue}
        </Typography>
      </Box>

      {/* Set Value Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="xs"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.7)'
            }
          }
        }}
        PaperProps={{
          sx: {
            backgroundColor: '#f5f5f5'
          }
        }}
      >
        <DialogTitle>
          Değer Ayarla
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {currentTagValue !== null && currentTagValue !== undefined && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Mevcut Değer
                </Typography>
                <Typography variant="h6" color="primary">
                  {formattedValue}{unit ? ' ' + unit : ''}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <TextField
              autoFocus
              fullWidth
              label="Yeni Değer"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={placeholder}
              variant="outlined"
              sx={{ mt: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>
            İptal
          </Button>
          <Button
            onClick={handleSetValue}
            variant="contained"
            disabled={inputValue === ''}
            endIcon={<SendIcon />}
          >
            {buttonText}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SetValue;
